import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'; // Import useNavigate and useLocation
import { Helmet } from 'react-helmet-async'; // Import Helmet for SEO
import Layout from '../../components/ui/Layout';
import { db } from '../../firebase';
import { collection, doc, getDoc, getDocs, query, where, runTransaction, addDoc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../components/context/AuthContext';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation'; // Import Breadcrumb
import ShareButton from '../../components/ui/ShareButton';
import { SITE_URL, SITE_NAME, getProductShareSlug } from '../../constants/site';
import { Button, Container, Row, Col, Form, Image, Modal, ListGroup, Badge } from 'react-bootstrap';
import { createLogger } from '../../utils/logger';
import { useNotification } from '../../components/ui/Notification';
import { formatCartItems, calculateTotalItems, calculateTotalPrice, getActiveCartInstanceId } from '../../utils/cartHelpers';
import {
  effectiveDisplayPrice,
  discountDealsForType,
  quantityForType,
  totalQuantityForInventory,
  totalDealsCountForInventory,
  typePriceFromMap,
} from '../../utils/inventoryPricing';

const logger = createLogger('SupermarketProducts');

function cardPriceLabel(product) {
  const inv = product.inventoryDetails;
  const types = product.product_types || [];
  const tp = inv?.type_prices || {};
  const nums = types
    .map((t) => (t.id ? typePriceFromMap(tp, t.id) : null))
    .filter((n) => n != null && !Number.isNaN(n) && n > 0);
  if (nums.length > 1) return `From $${Math.min(...nums).toFixed(2)}`;
  if (nums.length === 1) return `$${nums[0].toFixed(2)}`;
  const e = effectiveDisplayPrice(inv);
  return e != null ? `$${e.toFixed(2)}` : 'N/A';
}

// ProductImage component (assuming it's correctly defined as before)
const ProductImage = ({ imagePath, altText }) => {
  const [imageUrl, setImageUrl] = useState('');
  const storage = getStorage();
  useEffect(() => {
    if (imagePath && typeof imagePath === 'string' && !imagePath.startsWith('blob:')) {
      const imageRef = ref(storage, imagePath);
      getDownloadURL(imageRef)
        .then(setImageUrl)
        .catch(err => logger.error("[ProductImage] Error fetching image URL for path:", imagePath, err));
    } else if (imagePath && typeof imagePath === 'string' && imagePath.startsWith('blob:')) {
        logger.warn("[ProductImage] Attempted to use a blob URL with Firebase Storage ref. Path:", imagePath);
        setImageUrl('');
    } else if (imagePath) {
        logger.warn("[ProductImage] imagePath is not a string or is invalid:", imagePath);
        setImageUrl('');
    }
  }, [imagePath, storage]);

  if (imagePath && typeof imagePath === 'string' && imagePath.startsWith('blob:') && !imageUrl) {
    return <div className="card-img-top" style={{height: '200px', backgroundColor: '#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center'}}><span>Blob Image (Local Preview)</span></div>;
  } 
  if (!imageUrl) return <div className="card-img-top" style={{height: '200px', backgroundColor: '#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center'}}><span>Loading Image...</span></div>;
  return <img src={imageUrl} className="card-img-top" alt={altText} style={{height: '200px', objectFit: 'cover'}}/>;
};

// Helper to check if user can moderate reviews for this supermarket
function canModerateReviews(userRole, currentUser, supermarket, employeeSupermarketId) {
  if (!userRole || !currentUser || !supermarket) return false;
  if (userRole === 'Admin') return true;
  if (userRole === 'Employee') {
    // Use employeeSupermarketId from AuthContext
    if (employeeSupermarketId && employeeSupermarketId === supermarket.id) {
      return true;
    }
  }
  return false;
}

// Helper to check if user can see hidden reviews for this supermarket
function canSeeHiddenReviews(userRole, currentUser, supermarket, employeeSupermarketId) {
  return canModerateReviews(userRole, currentUser, supermarket, employeeSupermarketId);
}

export default function SupermarketProducts() {
  const { hardwareId, seoName, slug } = useParams();
  const supermarketId = hardwareId;
  const effectiveSeoName = seoName ?? slug; 
  const [products, setProducts] = useState([]);
  const [supermarket, setSupermarket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seoSupermarketId, setSeoSupermarketId] = useState(null);
  const { currentUser, userRole, employeeSupermarketId, userName } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate(); // For Back button
  const location = useLocation(); // For SEO URL handling
  
  // Debug logging for routing
  useEffect(() => {
    logger.debug('[SupermarketProducts] Component mounted with params:', { hardwareId, effectiveSeoName });
    logger.debug('[SupermarketProducts] Current location:', location.pathname);
  }, [supermarketId, effectiveSeoName, location.pathname]);

  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviews, setReviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [hidingReviewIds, setHidingReviewIds] = useState([]);
  const [supermarketImage, setSupermarketImage] = useState(''); // For supermarket image
  
  // Discount deals modal state
  const [showDealsModal, setShowDealsModal] = useState(false);
  const [selectedProductDeals, setSelectedProductDeals] = useState([]);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedProductPrice, setSelectedProductPrice] = useState(0);
  const [showTypePickModal, setShowTypePickModal] = useState(false);
  const [typePickState, setTypePickState] = useState(null);
  const [typePickSelectedId, setTypePickSelectedId] = useState('');
  const [showDealsTypePickModal, setShowDealsTypePickModal] = useState(false);
  const [dealsTypePickState, setDealsTypePickState] = useState(null);
  const [dealsTypePickSelectedId, setDealsTypePickSelectedId] = useState('');

  // Pagination states
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const productsPerPage = 12; // 3 rows × 4 items = 12 products

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { path: "/hardware", label: "Hardware stores" },
    { label: supermarket?.display_name || "Hardware store products" } 
  ];

  const openDealsModalForProduct = (product, typeId) => {
    const inv = product.inventoryDetails;
    setSelectedProductDeals(discountDealsForType(inv, typeId));
    setSelectedProductName(product.display_name || 'Unknown Product');
    setSelectedProductPrice(
      typeId ? typePriceFromMap(inv?.type_prices, typeId) || effectiveDisplayPrice(inv) || 0 : effectiveDisplayPrice(inv) || 0
    );
    setShowDealsModal(true);
  };

  const handleViewDeals = (product) => {
    const inv = product.inventoryDetails;
    const types = product.product_types || [];
    const tp = inv?.type_prices || {};
    const offered = types.filter((t) => t.id && typePriceFromMap(tp, t.id) != null);
    if (offered.length > 1) {
      setDealsTypePickState({ product, offered });
      setDealsTypePickSelectedId(offered[0].id);
      setShowDealsTypePickModal(true);
      return;
    }
    const typeId = offered.length === 1 ? offered[0].id : null;
    openDealsModalForProduct(product, typeId);
  };

  const confirmDealsTypePick = () => {
    if (!dealsTypePickState) return;
    const { product, offered } = dealsTypePickState;
    const t = offered.find((x) => x.id === dealsTypePickSelectedId);
    setShowDealsTypePickModal(false);
    setDealsTypePickState(null);
    if (t) openDealsModalForProduct(product, t.id);
  };

  const handleCloseDealsModal = () => {
    setShowDealsModal(false);
    setSelectedProductDeals([]);
    setSelectedProductName('');
    setSelectedProductPrice(0);
  };

  const performAddToCart = async (productId, productDetails, { lineName, price, productTypeId, productTypeName }) => {
    if (!currentUser) {
      alert("Please log in to add items to your cart.");
      navigate(`/login?path=${  window.location.pathname}`);
      return;
    }
    if (!supermarket) {
        alert("Supermarket details not loaded yet. Please wait.");
        return;
    }
    const inv = productDetails.inventoryDetails;
    const stock = productTypeId ? quantityForType(inv, productTypeId) : quantityForType(inv, null);
    if (stock <= 0) {
      alert('This product is out of stock.');
      return;
    }
    const userId = currentUser.uid;
    const cartRef = doc(db, 'Carts', userId);
    const typeKey = productTypeId || '';
    const newProductEntry = {
        productId,
        quantity: 1,
        name: lineName,
        price,
        ...(productTypeId ? { productTypeId, productTypeName } : {}),
    };
    try {
      let updatedCartData = null;
      
      await runTransaction(db, async (t) => {
        const cartDoc = await t.get(cartRef);
        let cartData = {}; 
        
        if (cartDoc.exists) {
            cartData = cartDoc.data();
            if (typeof cartData !== 'object' || cartData === null) cartData = {}; 
        }
        
        let supermarketCartInstances = cartData[supermarketId] || {};
        if (typeof supermarketCartInstances !== 'object' || Array.isArray(supermarketCartInstances) || supermarketCartInstances === null) {
            supermarketCartInstances = {};
        }
        
        let activeCartInstanceId = null;
        let activeCartInstance = null;
        for (const instanceId in supermarketCartInstances) {
          if (supermarketCartInstances.hasOwnProperty(instanceId) && supermarketCartInstances[instanceId] && supermarketCartInstances[instanceId].status === 'active') {
            activeCartInstanceId = instanceId;
            activeCartInstance = supermarketCartInstances[instanceId];
            break;
          }
        }
        
        if (activeCartInstance) {
          const productIndex = activeCartInstance.products.findIndex(
            (p) => p.productId === productId && (p.productTypeId || '') === typeKey
          );
          if (productIndex > -1) {
            activeCartInstance.products[productIndex].quantity += 1;
          } else {
            activeCartInstance.products.push(newProductEntry);
          }
          activeCartInstance.lastUpdatedAt = serverTimestamp();
          activeCartInstance.hasUnseenUpdate = true;
        } else {
          activeCartInstanceId = `cart_instance_${Date.now()}`;
          activeCartInstance = {
            instanceId: activeCartInstanceId,
            products: [newProductEntry],
            status: 'active',
            supermarketName: supermarket.display_name,
            createdAt: serverTimestamp(),
            lastUpdatedAt: serverTimestamp(),
            hasUnseenUpdate: true,
          };
        }
        
        supermarketCartInstances[activeCartInstanceId] = activeCartInstance;
        const updateData = { 
          ...cartData, 
          [supermarketId]: supermarketCartInstances, 
          lastGlobalUpdate: serverTimestamp() 
        };
        
        t.set(cartRef, updateData, { merge: true });
        
        updatedCartData = activeCartInstance ? { [activeCartInstanceId]: activeCartInstance } : {};
      });
      
      const addedItem = {
        name: lineName,
        quantity: 1,
        price
      };
      
      const activeCartInstanceId = getActiveCartInstanceId(updatedCartData);
      
      showNotification({
        type: 'cart',
        title: 'Item Added to Cart',
        message: '',
        duration: 6000,
        cartData: {
          addedItem,
          cartItems: formatCartItems(updatedCartData),
          totalItems: calculateTotalItems(updatedCartData),
          totalPrice: calculateTotalPrice(updatedCartData),
          supermarketName: supermarket.display_name
        },
        onViewCart: () => navigate('/carts'),
        onCheckout: () => {
          if (activeCartInstanceId) {
            navigate(`/checkout/${supermarketId}/${activeCartInstanceId}`);
          } else {
            navigate(`/checkout/${supermarketId}`);
          }
        },
        userId: currentUser.uid,
        supermarketId: supermarketId
      });
    } catch (error) {
      logger.error("Error adding to cart: ", error);
      alert(`Failed to add product to cart. ${  error.message}`);
    }
  };

  const beginAddToCart = (productId, productDetails) => {
    if (!currentUser) {
      alert("Please log in to add items to your cart.");
      navigate(`/login?path=${  window.location.pathname}`);
      return;
    }
    if (!supermarket) {
      alert("Supermarket details not loaded yet. Please wait.");
      return;
    }
    const types = productDetails.product_types || [];
    const tp = productDetails.inventoryDetails?.type_prices || {};
    const offered = types.filter((t) => t.id && typePriceFromMap(tp, t.id) != null);
    if (offered.length === 0) {
      const p = effectiveDisplayPrice(productDetails.inventoryDetails);
      if (p == null || Number.isNaN(p)) {
        alert('Price is not available for this product.');
        return;
      }
      performAddToCart(productId, productDetails, {
        lineName: productDetails.display_name,
        price: p,
        productTypeId: null,
        productTypeName: null,
      });
      return;
    }
    if (offered.length === 1) {
      const t = offered[0];
      performAddToCart(productId, productDetails, {
        lineName: `${productDetails.display_name} — ${t.name}`,
        price: typePriceFromMap(tp, t.id),
        productTypeId: t.id,
        productTypeName: t.name,
      });
      return;
    }
    setTypePickState({ productId, productDetails, offered });
    setTypePickSelectedId(offered[0].id);
    setShowTypePickModal(true);
  };

  const confirmTypePickAndAdd = async () => {
    if (!typePickState) return;
    const { productId, productDetails, offered } = typePickState;
    const t = offered.find((x) => x.id === typePickSelectedId);
    if (!t) return;
    const tp = productDetails.inventoryDetails.type_prices;
    setShowTypePickModal(false);
    setTypePickState(null);
    await performAddToCart(productId, productDetails, {
      lineName: `${productDetails.display_name} — ${t.name}`,
      price: typePriceFromMap(tp, t.id),
      productTypeId: t.id,
      productTypeName: t.name,
    });
  };

  const handleHideReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to hide this review?')) return;
    setHidingReviewIds(ids => [...ids, reviewId]);
    try {
      await updateDoc(doc(db, 'SupermarketReviews', reviewId), { hidden: true });
    } catch (err) {
      alert(`Failed to hide review: ${  err.message}`);
    }
    setHidingReviewIds(ids => ids.filter(id => id !== reviewId));
  };

  const handleUnhideReview = async (reviewId) => {
    if (!window.confirm('Make this review visible to all users?')) return;
    setHidingReviewIds(ids => [...ids, reviewId]);
    try {
      await updateDoc(doc(db, 'SupermarketReviews', reviewId), { hidden: false });
    } catch (err) {
      alert(`Failed to unhide review: ${  err.message}`);
    }
    setHidingReviewIds(ids => ids.filter(id => id !== reviewId));
  };

  // Handle SEO routing - find supermarket by SEO name if provided
  useEffect(() => {
    const findSupermarketBySeoName = async () => {
      if (effectiveSeoName && !supermarketId) {
        try {
          // Query supermarkets by SEO name
          const supermarketsRef = collection(db, 'Supermarkets');
          const seoQuery = query(supermarketsRef, where('seo_name', '==', effectiveSeoName));
          const seoSnapshot = await getDocs(seoQuery);
          
          if (!seoSnapshot.empty) {
            const foundSupermarket = seoSnapshot.docs[0];
            setSeoSupermarketId(foundSupermarket.id);
            
            // Update URL to include the actual supermarket ID for consistency
            const newPath = `/hardware/${foundSupermarket.id}/products/${effectiveSeoName}`;
            if (location.pathname !== newPath) {
              navigate(newPath, { replace: true });
            }
          } else {
            // SEO name not found, redirect to 404 or search
            navigate('/hardware', { replace: true });
          }
        } catch (error) {
          logger.error("Error finding supermarket by SEO name:", error);
          navigate('/hardware', { replace: true });
        }
      }
    };

    findSupermarketBySeoName();
  }, [effectiveSeoName, supermarketId, navigate, location.pathname]);

  // Check if effectiveSeoName is actually a product ID and redirect if needed
  useEffect(() => {
    if (effectiveSeoName && supermarketId && !seoSupermarketId) {
      logger.debug('[SupermarketProducts] Checking effectiveSeoName:', effectiveSeoName, 'supermarketId:', supermarketId);
      
      // Check if effectiveSeoName looks like a product ID (Firebase document ID format)
      if (effectiveSeoName.length === 20 && /^[a-zA-Z0-9]+$/.test(effectiveSeoName)) {
        // This looks like a product ID, redirect to product details
        logger.debug('[SupermarketProducts] effectiveSeoName appears to be a product ID, redirecting to product details');
        navigate(`/hardware/${supermarketId}/products/${effectiveSeoName}`, { replace: true });
        return;
      }
      
      // Check if effectiveSeoName is actually a supermarket SEO name
      const checkIfSupermarketSeo = async () => {
        try {
          const supermarketsRef = collection(db, 'Supermarkets');
          const seoQuery = query(supermarketsRef, where('seo_name', '==', effectiveSeoName));
          const seoSnapshot = await getDocs(seoQuery);
          
          if (!seoSnapshot.empty) {
            // This is a supermarket SEO name, not a product ID
            const foundSupermarket = seoSnapshot.docs[0];
            if (foundSupermarket.id !== supermarketId) {
              // Different supermarket, redirect to the correct one
              navigate(`/hardware/${foundSupermarket.id}/products/${effectiveSeoName}`, { replace: true });
            }
          }
        } catch (error) {
          logger.error("Error checking if effectiveSeoName is supermarket SEO:", error);
        }
      };
      
      checkIfSupermarketSeo();
    }
  }, [effectiveSeoName, supermarketId, seoSupermarketId, navigate]);

  useEffect(() => {
    const fetchSupermarketDetails = async () => {
      const targetSupermarketId = seoSupermarketId || supermarketId;
      if (!targetSupermarketId) return;

      try {
        const supermarketDoc = await getDoc(doc(db, 'Supermarkets', targetSupermarketId));
        if (supermarketDoc.exists) {
          const supermarketData = { id: supermarketDoc.id, ...supermarketDoc.data() };
          setSupermarket(supermarketData);
          
          // Fetch supermarket image if available
          if (supermarketData.media && supermarketData.media.length > 0 && supermarketData.media[0].url) {
            try {
              const storage = getStorage();
              const imageRef = ref(storage, supermarketData.media[0].url);
              const imageUrl = await getDownloadURL(imageRef);
              setSupermarketImage(imageUrl);
            } catch (imageError) {
              logger.warn(`Failed to load supermarket image:`, imageError);
            }
          }
        } else {
          logger.error("Supermarket not found");
        }
      } catch (error) {
        logger.error("Error fetching supermarket details:", error);
      }
    };

    const fetchProducts = async () => {
      const targetSupermarketId = seoSupermarketId || supermarketId;
      if (!targetSupermarketId) return;
      
      setLoading(true);
      try {
        const inventoryRef = collection(db, 'Inventory');
        const inventoryQuery = query(inventoryRef, where('supermarket_id', '==', targetSupermarketId));
        const inventorySnapshot = await getDocs(inventoryQuery);
        if (inventorySnapshot.empty) {
          setProducts([]);
          return;
        }
        const productPromises = inventorySnapshot.docs.map(async (itemDoc) => {
          const itemData = itemDoc.data();
          if (!itemData.product_id) return null;
          const productDoc = await getDoc(doc(db, 'Products', itemData.product_id)); 
          if (productDoc.exists) {
            return { id: productDoc.id, ...productDoc.data(), inventoryDetails: itemData };
          }
          return null;
        });
        const fetchedProducts = (await Promise.all(productPromises)).filter(p => p && p.inventoryDetails && p.inventoryDetails.quantity > 0);
        setProducts(fetchedProducts);
        
        // Reset pagination when products change
        setCurrentPage(1);
        setDisplayedProducts([]);
      } catch (error) {
        logger.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSupermarketDetails().then(() => fetchProducts());
  }, [supermarketId, seoSupermarketId]);

  // Fetch reviews for this supermarket
  useEffect(() => {
    const targetSupermarketId = seoSupermarketId || supermarketId;
    if (!targetSupermarketId) return;
    const reviewsRef = collection(db, 'SupermarketReviews');
    const reviewsQuery = query(reviewsRef, where('supermarketId', '==', targetSupermarketId));
    const unsubscribe = onSnapshot(reviewsQuery, snapshot => {
        setReviews(
          snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(r => r.createdAt) // Only require a timestamp
            .sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.())
        );
      }, (error) => {
        if (error.code === 'permission-denied') {
          logger.warn('[SupermarketProducts] Reviews permission denied - ratings hidden');
          setReviews([]);
        } else {
          logger.error('[SupermarketProducts] Reviews listener error:', error);
        }
      });
    return () => unsubscribe();
  }, [supermarketId, seoSupermarketId]);

  // Submit review
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    const targetSupermarketId = seoSupermarketId || supermarketId;
    if (!targetSupermarketId) return;
    
    setReviewSubmitting(true);
    setReviewError('');
    try {
      await addDoc(collection(db, 'SupermarketReviews'), {
        supermarketId: targetSupermarketId,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: userName || currentUser.displayName || currentUser.email?.split('@')[0] || 'Anonymous',
        rating: reviewRating,
        review: reviewText,
        createdAt: serverTimestamp(),
      });
      setReviewText('');
      setReviewRating(5);
    } catch (err) {
      setReviewError(`Failed to submit review: ${  err.message}`);
    }
    setReviewSubmitting(false);
  };

  // Calculate average rating using all reviews (including hidden)
  const allReviews = reviews; // reviews already contains both visible and hidden if you fetch them all
  const allRatedReviews = allReviews.filter(r => typeof r.rating === 'number');
  const averageRating = allRatedReviews.length
    ? (allRatedReviews.reduce((sum, r) => sum + r.rating, 0) / allRatedReviews.length)
    : 0;

  // Auto-dismiss effect removed - now handled by Toast component with autohide

  // Pagination logic (search + category filter)
  useEffect(() => {
    const startIndex = 0;
    const endIndex = currentPage * productsPerPage;
    const filtered = products.filter(product => {
      const matchSearch =
        !searchTerm ||
        product.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.category || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchCategory =
        !categoryFilter || (product.category || 'Uncategorized') === categoryFilter;
      return matchSearch && matchCategory;
    });

    setDisplayedProducts(filtered.slice(startIndex, endIndex));
    setHasMoreProducts(endIndex < filtered.length);
  }, [products, currentPage, searchTerm, categoryFilter]);

  // Load more products when scrolling
  const loadMoreProducts = useCallback(() => {
    if (!loadingMore && hasMoreProducts) {
      setLoadingMore(true);
      setTimeout(() => {
        setCurrentPage(prev => prev + 1);
        setLoadingMore(false);
      }, 500); // Small delay for smooth UX
    }
  }, [loadingMore, hasMoreProducts]);

  // Scroll detection for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
        loadMoreProducts();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreProducts]);

  // Reset pagination when search term or category filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter]);

  // Unique categories from products (for filter dropdown)
  const categories = React.useMemo(() => {
    const set = new Set();
    products.forEach(p => set.add(p.category || 'Uncategorized'));
    return ['', ...Array.from(set).sort((a, b) => (a === 'Uncategorized' ? 1 : b === 'Uncategorized' ? -1 : a.localeCompare(b)))];
  }, [products]);

  if (loading) {
    return <Layout><Container className="mt-4 text-center"><p>Loading products...</p></Container></Layout>;
  }

  // Group displayed products by category for the current page
  const productsByCategory = displayedProducts.reduce((acc, product) => {
    const category = product.category || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(product);
    return acc;
  }, {});

  // Generate SEO-friendly data
  const seoTitle = supermarket ? `${supermarket.display_name} - Products | ${SITE_NAME}` : `Hardware store products | ${SITE_NAME}`;
  const seoDescription = supermarket ? 
    `Browse products from ${supermarket.display_name}. ${supermarket.address ? `Located at ${supermarket.address}.` : ''} ${supermarket.delivery_enabled ? 'Delivery available.' : ''} Shop online for groceries and household items.` : 
    'Browse hardware store products online. Shop for groceries and household items with delivery and pickup options.';
  const seoKeywords = supermarket ? 
    `${supermarket.display_name}, supermarket, groceries, online shopping, delivery, pickup, ${supermarket.address ? supermarket.address.split(' ').join(', ') : ''}` : 
    'supermarket, groceries, online shopping, delivery, pickup';
  const seoImage = supermarketImage || `${SITE_URL}/logo192.png`;
  const seoUrl = supermarket?.seo_name ? 
    `${SITE_URL}/hardware/${supermarket.id}/products/${supermarket.seo_name}` : 
    `${SITE_URL}/hardware/${supermarket?.id}/products`;

  return (
    <>
      
      <Layout>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta name="author" content={SITE_NAME} />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content={SITE_NAME} />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={seoUrl} />
        <meta property="twitter:title" content={seoTitle} />
        <meta property="twitter:description" content={seoDescription} />
        <meta property="twitter:image" content={seoImage} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={seoUrl} />
        
        {/* Structured Data for Local Business */}
        {supermarket && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "GroceryStore",
              "name": supermarket.display_name,
              "address": supermarket.address ? {
                "@type": "PostalAddress",
                "streetAddress": supermarket.address
              } : undefined,
              "url": seoUrl,
              "image": seoImage,
              "description": seoDescription,
              "aggregateRating": allRatedReviews.length > 0 ? {
                "@type": "AggregateRating",
                "ratingValue": averageRating,
                "reviewCount": allRatedReviews.length,
                "bestRating": 5,
                "worstRating": 1
              } : undefined,
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Products",
                "itemListElement": products.slice(0, 10).map(product => ({
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Product",
                    "name": product.display_name,
                    "description": product.description,
                    "image": product.media?.[0]?.url
                  },
                  "price": effectiveDisplayPrice(product.inventoryDetails) ?? product.inventoryDetails?.sale_price,
                  "priceCurrency": "JMD",
                  "availability": totalQuantityForInventory(product.inventoryDetails) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
                }))
              }
            })}
          </script>
        )}
      </Helmet>

      <Container className="mt-4 px-3">
        <Row className="align-items-center mb-1">
          <Col xs="auto">
            <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
              &larr; Go Back
            </Button>
          </Col>
          <Col>
            <BreadcrumbNavigation items={breadcrumbItems} />
          </Col>
        </Row>

        {/* Supermarket Header with Image */}
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-4">
          {/* Supermarket Image */}
          <div className="me-0 me-md-4 mb-3 mb-md-0" style={{ width: '100px', height: '100px', flexShrink: 0 }}>
            {supermarketImage ? (
              <Image 
                src={supermarketImage} 
                alt={supermarket?.display_name || 'Supermarket'} 
                className="img-fluid rounded shadow-sm"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  logger.warn('Supermarket image failed to load');
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div 
                className="d-flex align-items-center justify-content-center bg-light rounded shadow-sm"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  fontSize: '0.8rem',
                  color: '#6c757d'
                }}
              >
                No Image
              </div>
            )}
          </div>
          
          {/* Supermarket Details */}
          <div className="flex-grow-1 w-100">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
              <h2 className="mb-0 text-break fs-4 fs-md-3">{supermarket?.display_name || 'Supermarket'} - Products</h2>
              <ShareButton
                url={seoUrl}
                title={seoTitle}
                text={seoDescription}
                variant="outline-secondary"
                size="sm"
              />
            </div>
            
            {/* Supermarket Address */}
            {supermarket?.address && (
              <p className="mb-2 text-muted text-break small" style={{ fontSize: '1rem' }}>
                <strong>Address:</strong> {supermarket.address}
              </p>
            )}
            
            {/* Delivery Service Indicator */}
            {supermarket?.delivery_enabled && (
              <div className="mb-2">
                <span className="badge bg-success text-white me-2">
                  <i className="fas fa-truck me-1"></i>
                  Delivery Available
                </span>
                <small className="text-muted">This supermarket offers delivery service for orders</small>
              </div>
            )}
            
            {/* Opening Hours */}
            {supermarket?.opening_hours && supermarket.opening_hours.length > 0 && (
              <div className="mb-2">
                <strong className="d-block mb-1">Opening Hours:</strong>
                <div className="ms-3">
                  {supermarket.opening_hours.map((hour, idx) => (
                    <div key={idx} className="small text-muted mb-1">
                      <span className="fw-semibold">{hour.day}:</span> {hour.hours}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Show average rating if there are reviews */}
        <div className="mb-3">
          <strong>Supermarket Rating:&nbsp;</strong>
          {allRatedReviews.length > 0 ? (
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-1">
              <span style={{ color: '#f5c518', fontSize: '1.2em' }}>
                {'★'.repeat(Math.round(averageRating))}
                {'☆'.repeat(5 - Math.round(averageRating))}
              </span>
              <span className="text-muted small">
                {averageRating.toFixed(1)} / 5 ({allRatedReviews.length} review{allRatedReviews.length > 1 ? 's' : ''})
              </span>
            </div>
          ) : (
            <span className="text-muted">No ratings yet</span>
          )}
        </div>

        {/* --- Search Bar and Category Filter --- */}
        <Row className="mb-4 align-items-center g-2">
          <Col xs={12} md={6}>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-100"
                style={{ fontSize: '16px' }} // Prevents zoom on iOS
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm('')}
                  style={{
                    fontSize: '16px',
                    minWidth: '44px',
                    position: 'absolute',
                    right: '0',
                    top: '0',
                    height: '100%',
                    borderTopLeftRadius: '0',
                    borderBottomLeftRadius: '0',
                    zIndex: 10
                  }}
                >
                  ×
                </Button>
              )}
            </div>
          </Col>
          <Col xs={12} md={4} lg={3}>
            <Form.Select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              aria-label="Filter by category"
              style={{ fontSize: '16px' }}
            >
              <option value="">All categories</option>
              {categories.filter(c => c !== '').map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        {!supermarket && !loading && (
            <p>Supermarket details could not be loaded. Products may be unavailable.</p>
        )}

        {Object.keys(productsByCategory).length === 0 && !loading ? (
          <p>No products found for this supermarket{searchTerm || categoryFilter ? ' with the current search or category filter.' : '.'}</p>
        ) : (
          (() => {
            // Sort categories (Uncategorized last)
            const sortedCategories = Object.entries(productsByCategory).sort(([a], [b]) => {
              if (a === 'Uncategorized') return 1;
              if (b === 'Uncategorized') return -1;
              return a.localeCompare(b);
            });

            // Flatten all products with their categories for space filling
            const allProducts = [];
            sortedCategories.forEach(([category, products]) => {
              products.forEach(product => {
                allProducts.push({ ...product, category });
              });
            });

            // Group products into rows of 4 (or 3 on medium screens, 2 on small, 1 on xs)
            const productsPerRow = 4;
            const productRows = [];
            for (let i = 0; i < allProducts.length; i += productsPerRow) {
              productRows.push(allProducts.slice(i, i + productsPerRow));
            }

            return (
              <div>
                {productRows.map((row, rowIndex) => (
                  <Row key={rowIndex} xs={1} sm={2} md={3} lg={4} className="g-3 mb-4">
                    {row.map((product) => {
                      const inv = product.inventoryDetails;
                      const dealCount = inv ? totalDealsCountForInventory(inv) : 0;
                      const stockTotal = inv ? totalQuantityForInventory(inv) : 0;
                      return (
                      <Col key={product.id}>
                        <div className="card h-100 shadow-sm">
                          <Link to={`/hardware/${supermarketId}/products/${getProductShareSlug(product)}`} style={{ textDecoration: 'none', color: 'inherit', display:'flex', flexDirection:'column', flexGrow:1 }}>
                            {product.media && product.media.length > 0 && product.media[0].url ? (
                              <ProductImage 
                                imagePath={product.media[0].url} 
                                altText={product.display_name} 
                              />
                            ) : (
                              <div className="card-img-top" style={{height: '180px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                <span className="small">No Image</span>
                              </div>
                            )}
                            <div className="card-body d-flex flex-column">
                              <div className="mb-2 d-flex flex-column gap-1">
                                <span className="badge bg-secondary text-white small align-self-start">{product.category}</span>
                                {dealCount > 0 && (
                                  <Badge bg="success" className="small align-self-start">
                                    {dealCount} Deal{dealCount !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                              </div>
                              <h5 className="card-title text-break small">{product.display_name || 'Unnamed Product'}</h5>
                              <p className="card-text mb-1">Price: {cardPriceLabel(product)}</p>
                              <p className="card-text mb-auto"><small className="text-muted">Stock: {inv ? stockTotal : 'N/A'}</small></p>
                            </div>
                          </Link>
                          <div className="card-footer bg-transparent border-top-0 p-2">
                            <div className="d-grid gap-2">
                              <Button 
                                variant="primary" 
                                className="btn-sm"
                                onClick={() => beginAddToCart(product.id, product)}
                                disabled={!inv || stockTotal <= 0 || !currentUser}
                              >
                                {currentUser ? (inv && stockTotal > 0 ? 'Add to Cart' : 'Out of Stock') : 'Login to Add'}
                              </Button>
                              {dealCount > 0 && (
                                <Button 
                                  variant="outline-success" 
                                  size="sm"
                                  onClick={() => handleViewDeals(product)}
                                  className="btn-sm"
                                >
                                  View Deals ({dealCount})
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </Col>
                      );
                    })}
                  </Row>
                ))}
                
                {/* Loading indicator for more products */}
                {loadingMore && (
                  <Row className="justify-content-center mt-4">
                    <Col xs="auto">
                      <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading more products...</p>
                      </div>
                    </Col>
                  </Row>
                )}
                
                {/* End of products indicator */}
                {!hasMoreProducts && displayedProducts.length > 0 && (
                  <Row className="justify-content-center mt-4">
                    <Col xs="auto">
                      <p className="text-muted text-center">No more products to load</p>
                    </Col>
                  </Row>
                )}
              </div>
            );
          })()
        )}

        {/* Reviews Section */}
        <hr className="my-4" />
        <h3>Supermarket Reviews</h3>
        {(() => {
  // For regular users, only show visible reviews
  // For Admins/Assigned Employees, show all reviews (hidden and visible)
  const visibleToAll = reviews.filter(r => !r.hidden);
  const visibleToModerator = canSeeHiddenReviews(userRole, currentUser, supermarket, employeeSupermarketId) ? reviews : visibleToAll;

  if (visibleToModerator.length === 0) return <p>No reviews yet.</p>;

  return visibleToModerator.map(r => (
    <div
      key={r.id}
      className={`mb-3 p-3 border rounded ${r.hidden ? 'bg-warning-subtle' : 'bg-light'}`}
      style={r.hidden ? { opacity: 0.7 } : {}}
    >
      <strong>
        {r.userName || r.userEmail?.split('@')[0] || 'Anonymous'}
      </strong>
      &nbsp;
      <span>
        {'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}
      </span>
      <div>{r.review}</div>
      <small className="text-muted">{r.createdAt?.toDate?.().toLocaleString?.() || ''}</small>
      {r.hidden && canSeeHiddenReviews(userRole, currentUser, supermarket, employeeSupermarketId) && (
        <span className="badge bg-warning text-dark ms-2">Hidden</span>
      )}
      {canModerateReviews(userRole, currentUser, supermarket, employeeSupermarketId) && (
        <>
          {!r.hidden ? (
            <Button
              variant="outline-danger"
              size="sm"
              className="ms-2"
              onClick={() => handleHideReview(r.id)}
              disabled={hidingReviewIds.includes(r.id)}
            >
              {hidingReviewIds.includes(r.id) ? 'Hiding...' : 'Hide'}
            </Button>
          ) : (
            <Button
              variant="outline-success"
              size="sm"
              className="ms-2"
              onClick={() => handleUnhideReview(r.id)}
              disabled={hidingReviewIds.includes(r.id)}
            >
              {hidingReviewIds.includes(r.id) ? 'Making visible...' : 'Make Visible'}
            </Button>
          )}
        </>
      )}
    </div>
  ));
})()}

        {/* Leave a Review */}
        {currentUser && (
          <Form onSubmit={handleReviewSubmit} className="mt-4 mb-5">
            <Form.Group controlId="reviewRating" className="mb-3">
              <Form.Label>Rating: {reviewRating} Star{reviewRating > 1 ? 's' : ''}</Form.Label>
              <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-3">
                <Form.Range
                  min={1}
                  max={5}
                  step={1}
                  value={reviewRating}
                  onChange={e => setReviewRating(Number(e.target.value))}
                  className="flex-grow-1 w-100"
                  style={{ maxWidth: '100%' }}
                />
                <div className="text-warning text-center text-sm-start" style={{ fontSize: '1.1em', minWidth: '80px' }}>
                  {'★'.repeat(reviewRating)}{'☆'.repeat(5 - reviewRating)}
                </div>
              </div>
            </Form.Group>
            <Form.Group controlId="reviewText" className="mb-3">
              <Form.Label>Review</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={reviewText}
                onChange={e => setReviewText(e.target.value)}
                required
                placeholder="Write your review here..."
                className="w-100"
                style={{
                  minHeight: 80,
                  resize: 'vertical'
                }}
              />
            </Form.Group>
            {reviewError && <div className="text-danger mb-2 small">{reviewError}</div>}
            <Button type="submit" variant="success" className="w-100 w-sm-auto" disabled={reviewSubmitting}>
              {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </Form>
        )}
        {!currentUser && (
          <div className="mb-5">
            <p>
              <Link to="/login">Log in</Link> to leave a review.
            </p>
          </div>
        )}


        <Modal show={showTypePickModal} onHide={() => { setShowTypePickModal(false); setTypePickState(null); }} centered>
          <Modal.Header closeButton>
            <Modal.Title>Choose product type</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {typePickState?.offered?.length > 0 && (
              <Form>
                <Form.Group>
                  {typePickState.offered.map((t) => {
                    const tp = typePickState.productDetails.inventoryDetails?.type_prices || {};
                    return (
                      <Form.Check
                        key={t.id}
                        type="radio"
                        name="pick-type"
                        id={`pick-${t.id}`}
                        label={`${t.name} — $${typePriceFromMap(tp, t.id).toFixed(2)}`}
                        checked={typePickSelectedId === t.id}
                        onChange={() => setTypePickSelectedId(t.id)}
                        className="mb-2"
                      />
                    );
                  })}
                </Form.Group>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowTypePickModal(false); setTypePickState(null); }}>Cancel</Button>
            <Button variant="primary" onClick={confirmTypePickAndAdd}>Add to cart</Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showDealsTypePickModal}
          onHide={() => {
            setShowDealsTypePickModal(false);
            setDealsTypePickState(null);
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Choose type to view deals</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {dealsTypePickState?.offered?.length > 0 && (
              <Form>
                <Form.Group>
                  {dealsTypePickState.offered.map((t) => {
                    const tp = dealsTypePickState.product.inventoryDetails?.type_prices || {};
                    return (
                      <Form.Check
                        key={t.id}
                        type="radio"
                        name="pick-deals-type"
                        id={`pick-deals-${t.id}`}
                        label={`${t.name} — $${typePriceFromMap(tp, t.id).toFixed(2)}`}
                        checked={dealsTypePickSelectedId === t.id}
                        onChange={() => setDealsTypePickSelectedId(t.id)}
                        className="mb-2"
                      />
                    );
                  })}
                </Form.Group>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDealsTypePickModal(false);
                setDealsTypePickState(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmDealsTypePick}>
              View deals
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Discount Deals Modal */}
        <Modal show={showDealsModal} onHide={handleCloseDealsModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Discount Deals for {selectedProductName}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedProductDeals.length === 0 ? (
              <p>No discount deals currently available for this item.</p>
            ) : (
              <div>
                <p className="text-muted mb-3">
                  <strong>Regular Price:</strong> ${selectedProductPrice.toFixed(2)} per item
                </p>
                <ListGroup>
                  {selectedProductDeals.map((deal, index) => {
                    const regularPrice = selectedProductPrice * deal.quantity;
                    const savings = regularPrice - deal.price;
                    const savingsPercentage = ((savings / regularPrice) * 100).toFixed(1);
                    return (
                      <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <strong className="fs-5">{deal.quantity} items</strong>
                              <br />
                              <span className="text-success fw-bold fs-4">${deal.price.toFixed(2)}</span>
                            </div>
                            <Badge bg="success" className="fs-6">
                              Save {savingsPercentage}%
                            </Badge>
                          </div>
                          <div className="text-muted small">
                            <div>Regular price: ${regularPrice.toFixed(2)}</div>
                            <div>You save: <span className="text-success fw-bold">${savings.toFixed(2)}</span></div>
                            <div>Per item: <span className="text-success fw-bold">${(deal.price / deal.quantity).toFixed(2)}</span></div>
                          </div>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
                <div className="mt-3 p-3 bg-light rounded">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    These deals are automatically applied when you add the specified quantity to your cart.
                  </small>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDealsModal}>Close</Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
    </>
  );
}
